import { useEffect, useState, type ComponentType } from "react"
import { CheckCircle2, Loader2 } from "lucide-react"
import type { AppShell } from "@/App"
import { Card, CardContent } from "@/components/ui/card"
import { getChargeProducts, getRondaConfig, saveChargeProducts, saveRondaConfig } from "./api"
import { toRequestError } from "./request-error"
import { ActionBarSpacer } from "./ui"

/**
 * Configurações do Armazém (Fase 2d) — só admins por Cargo; o servidor volta a
 * verificar (403). Cada secção é um cartão com título, descrição e o seu
 * «Guardar». Para acrescentar uma (alertas, notificações, regras de leitura):
 * um componente novo e uma entrada em `SECTIONS`.
 */
interface SectionProps {
  shell: AppShell
}

interface SectionDef {
  key: string
  title: string
  description: string
  Component: ComponentType<SectionProps>
}

const SECTIONS: SectionDef[] = [
  {
    key: "encargos",
    title: "Encargos na fatura de fornecedor",
    description:
      "Os portes, o ecovalor e outros encargos entram na fatura de fornecedor do Odoo como linhas à parte, cada um no seu artigo.",
    Component: ChargesSection,
  },
  {
    key: "ronda",
    title: "Ronda de stock",
    description:
      "Quem lê aprova no «Rever» e «Enviar» aplica no Odoo. As rondas «Tudo da localização» (o que não se lê vai a zero) são só para os responsáveis do armazém.",
    Component: RondaSection,
  },
]

export function ArmazemSettings({ shell }: { shell: AppShell }) {
  if (shell.isAdmin === undefined) return <p className="p-4 text-base text-muted-foreground">A carregar…</p>
  if (!shell.isAdmin) {
    return (
      <div className="mx-auto max-w-md p-8 text-center">
        <h1 className="text-lg font-semibold">Sem acesso</h1>
        <p className="mt-2 text-sm text-muted-foreground">As configurações do Armazém são só para administradores.</p>
      </div>
    )
  }
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Configurações</h1>
        <p className="mt-1 text-sm text-muted-foreground">Como o Armazém trata as receções e as rondas de stock. Só administradores veem esta página.</p>
      </div>
      {SECTIONS.map((s) => (
        <Card key={s.key} id={s.key}>
          <CardContent className="space-y-4 p-4 sm:p-6">
            <div>
              <h2 className="text-base font-semibold">{s.title}</h2>
              <p className="mt-1 text-sm text-muted-foreground">{s.description}</p>
            </div>
            <s.Component shell={shell} />
          </CardContent>
        </Card>
      ))}
      <ActionBarSpacer />
    </div>
  )
}

const CHARGES: Array<{ kind: string; label: string; example: string }> = [
  { kind: "portes", label: "Portes", example: "ex.: PORTES" },
  { kind: "ecovalor", label: "Ecovalor", example: "ex.: ECOVALOR" },
  { kind: "ecoreee", label: "Ecoreee", example: "ex.: ECOREEE" },
  { kind: "outro", label: "Outros encargos", example: "ex.: ENCARGOS" },
  { kind: "diversos", label: "Diversos (artigos que não existem no Odoo)", example: "ex.: PROD974" },
]

function ChargesSection({ shell }: SectionProps) {
  const [saved, setSaved] = useState<Record<string, string | null> | null>(null)
  const [value, setValue] = useState<Record<string, string | null>>({})
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)
  const [invalid, setInvalid] = useState<string[]>([])

  useEffect(() => {
    getChargeProducts(shell)
      .then((v) => {
        setSaved(v)
        setValue(v)
      })
      .catch((e) => setMessage({ ok: false, text: toRequestError(e).message }))
  }, [shell])

  if (!saved) return message ? <p className="text-sm text-red-600">{message.text}</p> : <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
  const dirty = CHARGES.some((c) => (value[c.kind] ?? "") !== (saved[c.kind] ?? ""))

  const save = async () => {
    setBusy(true)
    setMessage(null)
    setInvalid([])
    try {
      await saveChargeProducts(shell, value)
      setSaved(value)
      setMessage({ ok: true, text: "Guardado. Os artigos existem no Odoo." })
    } catch (e) {
      const err = toRequestError(e)
      setInvalid(((err.details as { kinds?: string[] } | undefined)?.kinds ?? []).map(String))
      setMessage({ ok: false, text: err.message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Referência interna do artigo no Odoo que recebe este encargo. Vazio = o encargo não é lançado e fica uma nota. «Diversos» é o artigo das linhas
        lançadas como Diversos na receção (vazio = sem essa opção).
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {CHARGES.map((c) => (
          <label key={c.kind} className="block">
            <span className="text-sm font-medium">{c.label}</span>
            <input
              value={value[c.kind] ?? ""}
              onChange={(e) => setValue({ ...value, [c.kind]: e.target.value || null })}
              placeholder={c.example}
              aria-label={`Artigo do Odoo para ${c.label}`}
              autoCapitalize="characters"
              spellCheck={false}
              aria-invalid={invalid.includes(c.kind)}
              className={`mt-1 h-12 w-full rounded-lg border bg-background px-3 font-mono text-base sm:h-10 sm:text-sm ${
                invalid.includes(c.kind) ? "border-red-400 ring-1 ring-red-300" : ""
              }`}
            />
            {invalid.includes(c.kind) && <span className="mt-1 block text-sm text-red-600">Artigo não encontrado no Odoo.</span>}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={busy || !dirty}
          className="inline-flex h-12 items-center gap-2 rounded-lg bg-primary px-5 text-base font-semibold text-primary-foreground disabled:opacity-50 sm:h-10 sm:text-sm"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Guardar
        </button>
        {message && (
          <span className={`flex items-center gap-1.5 text-sm ${message.ok ? "text-emerald-700" : "text-red-600"}`}>
            {message.ok && <CheckCircle2 className="h-4 w-4" />}
            {message.text}
          </span>
        )}
      </div>
    </div>
  )
}

function RondaSection({ shell }: SectionProps) {
  const [saved, setSaved] = useState<string | null>(null)
  const [teams, setTeams] = useState("")
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null)

  useEffect(() => {
    getRondaConfig(shell)
      .then((v) => {
        setSaved(v.responsible_teams.join(", "))
        setTeams(v.responsible_teams.join(", "))
      })
      .catch((e) => setMessage({ ok: false, text: toRequestError(e).message }))
  }, [shell])

  if (saved === null) return message ? <p className="text-sm text-red-600">{message.text}</p> : <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
  const list = teams.split(",").map((t) => t.trim()).filter(Boolean)
  const dirty = list.join(", ") !== saved

  const save = async () => {
    setBusy(true)
    setMessage(null)
    try {
      const out = await saveRondaConfig(shell, { responsible_teams: list })
      setSaved(out.responsible_teams.join(", "))
      setTeams(out.responsible_teams.join(", "))
      setMessage({ ok: true, text: "Guardado." })
    } catch (e) {
      setMessage({ ok: false, text: toRequestError(e).message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <label className="block">
        <span className="text-sm font-medium">Responsáveis do armazém</span>
        <span className="mt-1 block text-sm text-muted-foreground">
          Os administradores (Cargo) são sempre responsáveis. Para que também uma equipa faça rondas «Tudo da localização» (e abandone rondas grandes),
          escreva o identificador dela (ex.: chefes-armazem). Vazio = só administradores.
        </span>
        <input
          value={teams}
          onChange={(e) => setTeams(e.target.value)}
          placeholder="Só administradores"
          aria-label="Equipas dos responsáveis do armazém"
          spellCheck={false}
          className="mt-1 h-12 w-full rounded-lg border bg-background px-3 text-base sm:h-10 sm:text-sm"
        />
      </label>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={busy || !dirty}
          className="inline-flex h-12 items-center gap-2 rounded-lg bg-primary px-5 text-base font-semibold text-primary-foreground disabled:opacity-50 sm:h-10 sm:text-sm"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Guardar
        </button>
        {message && (
          <span className={`flex items-center gap-1.5 text-sm ${message.ok ? "text-emerald-700" : "text-red-600"}`}>
            {message.ok && <CheckCircle2 className="h-4 w-4" />}
            {message.text}
          </span>
        )}
      </div>
    </div>
  )
}
